import React, { useMemo, useState } from "react";
import Draggable from "react-draggable";
import "./DraggableComponent.css";
import CloseIcon from "@mui/icons-material/Close";
import LayersComponent from "../../molecules/layersComponent/LayersComponent";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import CustomDatePicker from "../../atoms/datePicker/CustomDatePicker";
import CustomTimePicker from "../../atoms/timePicker/CustomTimePicker";
import RestoreIcon from "@mui/icons-material/Restore";

import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { formatCountryLabel } from "../../../utils/countryLabel";
import DepartmentMultiSelect from "../../atoms/departmentMultiSelect/DepartmentMultiSelect";
import { MAX_RESTRICTED_DEPARTMENTS } from "../../../constants/defaults";

const LOWERCASE_WORDS = ["de", "du", "des", "la", "le", "et"];
const formatDepartmentLabel = (key) =>
  key
    .split("-")
    .map((word, i) =>
      LOWERCASE_WORDS.includes(word) && i !== 0
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join("-");

const DraggableComponent = ({
  handleDraggableComponentToggle,
  layers,
  opacities,
  onHandleOpacity,
  type,
  showComponent,
  onShowNavbarMenu,
  isSmallScreen,
  reducedDragWindow,
  onReduceDragWindow,
  weatherRequestDate,
  weatherRequestStartHour,
  setWeatherRequestDate,
  setWeatherRequestStartHour,
  weatherCountry,
  setWeatherCountry,
  vegetationCountry,
  setVegetationCountry,
  selectedVegDepartment,
  setSelectedVegDepartment,
  selectedWeatherDepartment,
  setSelectedWeatherDepartment,
  triggerTooltip,
  isDateTimeLocked,
}) => {
  const [activeDrags, setActiveDrags] = useState(0);
  const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 });
  const { getAllowedCountries, getAllowedDepartments } = useAccessRights();

  // Built directly from access rights
  const allowedVegetationCountries = useMemo(() => {
    const allowed = getAllowedCountries("display_layers_service");
    return allowed
      .map((key) => ({ value: key, label: formatCountryLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedCountries]);

  const departmentOptions = useMemo(() => {
    const allowed = getAllowedDepartments("display_layers_service");
    return allowed
      .map((key) => ({ value: key, label: formatDepartmentLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedDepartments]);

  const allowedWeatherCountries = useMemo(() => {
    const allowed = getAllowedCountries("weather");
    return allowed
      .map((key) => ({ value: key, label: formatCountryLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedCountries]);

  const weatherDepartmentOptions = useMemo(() => {
    const allowed = getAllowedDepartments("weather");
    return allowed
      .map((key) => ({ value: key, label: formatDepartmentLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedDepartments]);

  const showDepartmentFilter =
    type === "terrain" &&
    vegetationCountry === "france" &&
    departmentOptions.length > 0;

  const showWeatherDepartmentFilter =
    type === "meteo" &&
    weatherCountry === "france" &&
    weatherDepartmentOptions.length > 0;

  // Restricted = the user has access to French departments but not to all 95 of them. In that case, cap simultaneous selection at 5.
  const isVegDepartmentRestricted =
    departmentOptions.length > 0 && departmentOptions.length < 95;
  const isWeatherDepartmentRestricted =
    weatherDepartmentOptions.length > 0 && weatherDepartmentOptions.length < 95;

  const handleVegDepartmentChange = (newSelection) => {
    if (
      isVegDepartmentRestricted &&
      newSelection.length > MAX_RESTRICTED_DEPARTMENTS
    ) {
      triggerTooltip?.(
        `Vous ne pouvez sélectionner que ${MAX_RESTRICTED_DEPARTMENTS} départements en parallèle maximum.`,
      );
      return;
    }
    setSelectedVegDepartment(newSelection);
  };

  const handleWeatherDepartmentChange = (newSelection) => {
    if (
      isWeatherDepartmentRestricted &&
      newSelection.length > MAX_RESTRICTED_DEPARTMENTS
    ) {
      triggerTooltip?.(
        `Vous ne pouvez sélectionner que ${MAX_RESTRICTED_DEPARTMENTS} départements en parallèle maximum.`,
      );
      return;
    }
    setSelectedWeatherDepartment(newSelection);
  };
  const handleStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev + 1);
  };

  const handleCancel = () => {
    const now = new Date();
    const franceDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Paris" }),
    );

    const newDate = {
      day: franceDate.getDate().toString().padStart(2, "0"),
      month: (franceDate.getMonth() + 1).toString().padStart(2, "0"),
      year: franceDate.getFullYear().toString(),
    };

    setWeatherRequestDate(newDate);
    setWeatherRequestStartHour(franceDate.getHours());
  };

  const handleDrag = (e, data) => {
    e.stopPropagation();
    e.preventDefault();
    setDeltaPosition({
      x: deltaPosition.x + data.deltaX,
      y: deltaPosition.y + data.deltaY,
    });
  };
  const returnToMenu = () => {
    handleDraggableComponentToggle();
    onShowNavbarMenu();
  };
  const handleStop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev - 1);
  };
  const title = type === "meteo" ? "Météo" : "Couches";
  const positionClass = type === "meteo" ? "meteo-comp" : "draggable-comp";
  const hasActiveLayers = isDateTimeLocked ?? layers?.some((layer) =>
    layer.items?.some((item) => item.showItem),
  );
  const renderWeatherDepartmentFilter = () =>
    showWeatherDepartmentFilter && (
      <DepartmentMultiSelect
        options={weatherDepartmentOptions}
        selectedValues={selectedWeatherDepartment || []}
        onChange={handleWeatherDepartmentChange}
        disabled={hasActiveLayers}
        isSmallScreen={isSmallScreen}
        placeholder={isSmallScreen ? "Dép..." : "Sélectionner des dép."}
        fullAccessMode={!isWeatherDepartmentRestricted}
        maxCustom={MAX_RESTRICTED_DEPARTMENTS}
        triggerTooltip={triggerTooltip}
      />
    );
  const renderTerrainFilters = () => (
    <div
      className="select-container terrain-container"
      data-testid="terrain-controls"
    >
      <div className="terrain-filter-row">
        <select
          value={vegetationCountry}
          onChange={(e) => {
            const newCountry = e.target.value;
            setVegetationCountry(newCountry);
            if (newCountry === "france") {
              const allDeps = departmentOptions.map((d) => d.value);
              setSelectedVegDepartment?.(
                isVegDepartmentRestricted
                  ? allDeps.slice(0, MAX_RESTRICTED_DEPARTMENTS)
                  : allDeps,
              );
            } else {
              setSelectedVegDepartment?.([]);
            }
          }}
          disabled={hasActiveLayers}
          data-testid="terrain-country-select"
          onMouseDown={(e) => e.stopPropagation()}
          className="weather-country-select"
        >
          {allowedVegetationCountries.map((country) => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </select>

        {showDepartmentFilter && (
          <DepartmentMultiSelect
            options={departmentOptions}
            selectedValues={selectedVegDepartment || []}
            onChange={handleVegDepartmentChange}
            disabled={hasActiveLayers}
            isSmallScreen={isSmallScreen}
            placeholder={isSmallScreen ? "Dép..." : "Sélectionner des dép."}
            fullAccessMode={!isVegDepartmentRestricted}
            maxCustom={MAX_RESTRICTED_DEPARTMENTS}
            triggerTooltip={triggerTooltip}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      {isSmallScreen ? (
        <div
          className={`sidebar-draggableComp ${showComponent ? "open" : ""}`}
          data-testid={`draggable-panel-${type}`}
        >
          <div className="drag-comp-header">
            <KeyboardDoubleArrowLeftIcon
              onClick={returnToMenu}
              className="drag-comp-returnIcon"
              data-testid={`draggable-panel-${type}-return-btn`}
            />
            <div
              className="drag-comp-title"
              data-testid={`draggable-panel-${type}-title`}
            >
              {title}
            </div>
            <CloseIcon
              onClick={handleDraggableComponentToggle}
              className="drag-comp-closeIcon"
              data-testid={`draggable-panel-${type}-close-btn`}
            />
          </div>
          <div
            className="drag-comp-body"
            data-testid={`draggable-panel-${type}-body`}
          >
            {type === "meteo" && (
              <div className="select-container">
                {/* First Row: Date and Time */}

                <div className="select-row">
                  <select
                    value={weatherCountry}
                    onChange={(e) => {
                      const newCountry = e.target.value;
                      setWeatherCountry(newCountry);
                      if (newCountry === "france") {
                        const allDeps = weatherDepartmentOptions.map(
                          (d) => d.value,
                        );
                        setSelectedWeatherDepartment?.(
                          isWeatherDepartmentRestricted
                            ? allDeps.slice(0, MAX_RESTRICTED_DEPARTMENTS)
                            : allDeps,
                        );
                      } else {
                        setSelectedWeatherDepartment?.([]);
                      }
                    }}
                    disabled={hasActiveLayers}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="weather-country-select"
                    data-testid="meteo-country-select"
                  >
                    {allowedWeatherCountries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {renderWeatherDepartmentFilter()}
                </div>
                {/* Second Row: Country, Department*/}
                <div className="select-row">
                  <CustomDatePicker
                    weatherRequestDate={weatherRequestDate}
                    weatherRequestStartHour={weatherRequestStartHour}
                    onChange={(newDateObj) => setWeatherRequestDate(newDateObj)}
                    onHourChange={(newHour) =>
                      setWeatherRequestStartHour(newHour)
                    }
                    disabled={hasActiveLayers}
                    data-testid="meteo-date-picker"
                  />
                  <CustomTimePicker
                    weatherRequestStartHour={weatherRequestStartHour}
                    weatherRequestDate={weatherRequestDate}
                    onChange={(newHour) => setWeatherRequestStartHour(newHour)}
                    disabled={hasActiveLayers}
                    data-testid="meteo-time-picker"
                  />
                  <RestoreIcon
                    style={{ cursor: "pointer" }}
                    onClick={handleCancel}
                    disabled={hasActiveLayers}
                    className={`${hasActiveLayers ? "arrow-disabled" : ""}`}
                    data-testid="meteo-reset-datetime-btn"
                  />
                </div>
              </div>
            )}
            {type === "terrain" && renderTerrainFilters()}
            <LayersComponent
              opacities={opacities}
              onHandleOpacity={onHandleOpacity}
              layers={layers}
              type={type}
            />
          </div>
        </div>
      ) : (
        <Draggable
          handle=".handle"
          defaultPosition={{ x: -50, y: 50 }}
          position={null}
          grid={[25, 25]}
          scale={1}
          onStart={handleStart}
          onDrag={handleDrag}
          onStop={handleStop}
        >
          <div
            className={`${positionClass} handle`}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            data-testid={`draggable-panel-${type}`}
          >
            <div className="drag-comp-header">
              <div
                className="drag-comp-title"
                data-testid={`draggable-panel-${type}-title`}
              >
                {title}
              </div>
              <div className="drag-header-icons">
                {reducedDragWindow ? (
                  <CropSquareOutlinedIcon
                    onClick={onReduceDragWindow}
                    data-testid={`draggable-panel-${type}-expand-btn`}
                    className="drag-comp-closeIcon"
                    style={{ fontSize: "20px" }}
                  />
                ) : (
                  <FilterNoneOutlinedIcon
                    onClick={onReduceDragWindow}
                    className="drag-comp-closeIcon"
                    data-testid={`draggable-panel-${type}-reduce-btn`}
                    style={{ fontSize: "17px" }}
                  />
                )}
                <CloseIcon
                  onClick={handleDraggableComponentToggle}
                  data-testid={`draggable-panel-${type}-close-btn`}
                  className="drag-comp-closeIcon"
                  style={{ fontSize: "20px" }}
                />
              </div>
            </div>
            {!reducedDragWindow && (
              <div className="drag-comp-body">
                {type === "meteo" && (
                  <div className="select-container">
                    {/* First Row: Date and Time */}
                    <div className="select-row">
                      <select
                        value={weatherCountry}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          setWeatherCountry(newCountry);
                          if (newCountry === "france") {
                            const allDeps = weatherDepartmentOptions.map(
                              (d) => d.value,
                            );
                            setSelectedWeatherDepartment?.(
                              isWeatherDepartmentRestricted
                                ? allDeps.slice(0, MAX_RESTRICTED_DEPARTMENTS)
                                : allDeps,
                            );
                          } else {
                            setSelectedWeatherDepartment?.([]);
                          }
                        }}
                        disabled={hasActiveLayers}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="weather-country-select"
                        data-testid="meteo-country-select"
                      >
                        {allowedWeatherCountries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      {renderWeatherDepartmentFilter()}
                    </div>
                    {/* Second Row: Country, Department*/}

                    <div className="select-row">
                      <CustomDatePicker
                        weatherRequestDate={weatherRequestDate}
                        weatherRequestStartHour={weatherRequestStartHour}
                        onChange={(newDateObj) =>
                          setWeatherRequestDate(newDateObj)
                        }
                        onHourChange={(newHour) =>
                          setWeatherRequestStartHour(newHour)
                        }
                        disabled={hasActiveLayers}
                        data-testid="meteo-date-picker"
                      />
                      <CustomTimePicker
                        weatherRequestStartHour={weatherRequestStartHour}
                        weatherRequestDate={weatherRequestDate}
                        weatherRequestDate={weatherRequestDate}
                        onChange={(newHour) =>
                          setWeatherRequestStartHour(newHour)
                        }
                        disabled={hasActiveLayers}
                        data-testid="meteo-time-picker"
                      />
                      <RestoreIcon
                        style={{ cursor: "pointer" }}
                        onClick={handleCancel}
                        disabled={hasActiveLayers}
                        className={`${hasActiveLayers ? "arrow-disabled" : ""}`}
                        data-testid="meteo-reset-datetime-btn"
                      />
                    </div>
                  </div>
                )}
                {type === "terrain" && renderTerrainFilters()}
                <LayersComponent
                  opacities={opacities}
                  onHandleOpacity={onHandleOpacity}
                  layers={layers}
                />
              </div>
            )}
          </div>
        </Draggable>
      )}
    </>
  );
};

export default React.memo(DraggableComponent);
